import React, { Suspense, useEffect } from 'react';
import { Redirect, Route as ReactRouterRoute, Switch } from 'react-router-dom';
import Loading from 'components/shared-components/Loading';
import { AUTH_PREFIX_PATH } from 'configs/AppConfig';
import { withSentryRouting } from '@sentry/react';
import { resetCache } from '../../queryClient';
import { auth } from '../../auth/firebaseAuth';
import { authApi } from '../../api';
import store from '../../redux/store';
import { updateAuthState } from '../../redux/actions';
import { useQuery } from '../../utils/HoaUtils';
import { useAsync } from 'react-async-hook';
import { useAuthState } from '../../redux/selector';
import { lazyPreload } from '../app-views/components/LazyPreload';
import { EMAIL_QUERY_PARAM, useAuthMutation } from '../../hooks/Auth';
import { COMMUNITY_NAME_QUERY_PARAM } from './register';
import RecaptchaVerifierView from './components/RecaptchaVerifierView';
import { useResetUser } from '../../integration';

const LazyLogin = lazyPreload(() => import('views/auth-views/login'));
const LazyRegister = lazyPreload(() => import('views/auth-views/register'));
const LazyEmailLink = lazyPreload(() => import('views/auth-views/email-link'));
const LazyForgotPassword = lazyPreload(() => import('views/auth-views/forgot-password'));

auth.onAuthStateChanged(async (user) => {
  if (!user) {
    store.dispatch(
      updateAuthState({
        initialized: true,
      })
    );
    return;
  }
  const idToken = await user.getIdToken();
  if (idToken) {
    await authApi.registerWithFirebaseToken({
      firebaseSync: {
        idToken,
      },
    });
  }
  store.dispatch(
    updateAuthState({
      token: idToken,
      initialized: true,
    })
  );
});

const Route = withSentryRouting(ReactRouterRoute);

interface AuthViewsProps {}

const AuthViews: React.FC<AuthViewsProps> = () => {
  useResetCacheIfNeeded();
  useSignInIfNeeded();
  const defaultRoute = useDefaultRoute();
  const { token, redirect } = useAuthState();
  return (
    <>
      <RecaptchaVerifierView />
      <Suspense fallback={<Loading cover="page" />}>
        <Switch>
          {token && <Redirect to={redirect} />}
          <Route path={`${AUTH_PREFIX_PATH}/register`} component={LazyRegister} />
          <Route path={`${AUTH_PREFIX_PATH}/login`} component={LazyLogin} />
          <Route path={`${AUTH_PREFIX_PATH}/email-link`} component={LazyEmailLink} />
          <Route path={`${AUTH_PREFIX_PATH}/forgot-password`} component={LazyForgotPassword} />
          <Redirect from={`${AUTH_PREFIX_PATH}`} to={defaultRoute} />
        </Switch>
      </Suspense>
    </>
  );
};

const useResetCacheIfNeeded = () => {
  const resetUser = useResetUser();
  useEffect(() => {
    Promise.all([resetUser(), resetCache()]);
  }, [resetUser]);
};

const useSignInIfNeeded = () => {
  const { useSignInWithEmailLink } = useAuthMutation();
  const emailQueryParam = useQuery(EMAIL_QUERY_PARAM);
  useAsync(async () => {
    await useSignInWithEmailLink.mutateAsync(emailQueryParam);
  }, [emailQueryParam]);
};

const useDefaultRoute = () => {
  const isInvitedByAdmin = useIsInvitedByAdmin();
  if (isInvitedByAdmin) {
    return `${AUTH_PREFIX_PATH}/register`;
  }
  return `${AUTH_PREFIX_PATH}/login`;
};

const useIsInvitedByAdmin = () => {
  const { redirectQueryParams } = useAuthState();
  const query = new URLSearchParams(redirectQueryParams);
  return query.has(COMMUNITY_NAME_QUERY_PARAM);
};

export default AuthViews;
